import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Pagination,
    Paper,
    Typography,
    useTheme,
    Fade,
    Grow,
    Zoom,
    styled,
    Snackbar,
    Alert,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ProductionQuantityLimitsOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import ProductDetailModal from './ProductDetailModal';
import ProductCard from './ProductCard';
import { useMutation, gql } from '@apollo/client';
import { UPLOAD_IMAGES_API_URL } from '../constant';
import ProductForm from './AddProductForm';

const DELETE_PRODUCT = gql`
    mutation DeleteProduct($product_id: ID!) {
        deleteProduct(product_id: $product_id) {
            success
            message
        }
    }
`;

const UPDATE_PRODUCT = gql`
    mutation UpdateProduct($product_id: ID!, $product_name: String!, $product_description: String!) {
        updateProduct(product_id: $product_id, product_name: $product_name, product_description: $product_description) {
            success
            message
        }
    }
`;


const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[4],
    transition: 'all 0.3s ease',
}));

const AnimatedGrid = styled(Grid)({
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)'
    }
});





export default function FilteredProducts({
    filteredProducts,
    paginatedProducts,
    itemsPerPage,
    currentPage,
    handlePageChange,
    role,
    refresh,
    user,
}) {
    const { t } = useTranslation();
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    const theme = useTheme();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');


    const [formOpen, setFormOpen] = useState(false);


    const [product, setProduct] = useState(null);
    const [editProduct, setEditProduct] = useState(null);

    // Initialize edit form when product changes
    useEffect(() => {
        if (product) {
            setEditProduct({
                product_id: product.id,
                product_name: product.product_name,
                product_description: product.product_description,
                product_image: product.product_image // Reset image input
            });
        }
    }, [product]);
    const [deleteProduct] = useMutation(DELETE_PRODUCT, {
        onCompleted: (data) => {
            if (data.deleteProduct.success) {
                refresh();
            }
        },
        onError: (error) => {
            console.error('Error deleting product:', error);
        }
    });

    const [updateProduct] = useMutation(UPDATE_PRODUCT, {
        onCompleted: (data) => {
            if (data.updateProduct.success) {
                refresh();
            }
        },
        onError: (error) => {
            console.error('Error updating product:', error);
        }
    });

    const handleDelete = (product) => {
        deleteProduct({ variables: { product_id: product.id } })
            .then(({ data }) => {
                if (data.deleteProduct.success) {
                    setSnackbarMessage(t('productDeletedSuccessfully'));
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                } else {
                    setSnackbarMessage(t('errorDeletingProduct'));
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            })
            .catch((error) => {
                console.error('Error deleting product:', error);
                setSnackbarMessage(t('errorDeletingProduct'));
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            });
    };
    const handleSetProduct = (product) => {
        setProduct(product);
        setFormOpen(true);
    };
    const handleUpdateProduct = async (formData) => {
        try {
            // Update product details
            await updateProduct({
                variables: {
                    product_id: formData.product_id,
                    product_name: formData.product_name,
                    product_description: formData.product_description
                }
            });

            // Handle image upload if new image exists
            if (formData.product_image instanceof File) {
                const formDataImage = new FormData();
                formDataImage.append("image", formData.product_image);

                await fetch(`${UPLOAD_IMAGES_API_URL}/upload/product/${user.eventId}/${user.role}/${formData.product_id}`, {
                    method: 'POST',
                    body: formDataImage,
                });
            }

            setSnackbarMessage(t('productUpdatedSuccessfully'));
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            refresh();
        } catch (error) {
            console.error('Update error:', error);
            setSnackbarMessage(t('errorUpdatingProduct'));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };


    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };
    return (
        <Box sx={{ position: 'relative',p:0 }}>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <StyledPaper  elevation={3}>
                {filteredProducts.length === 0 ? (
                    <Fade in timeout={500}>
                        <Box
                            minHeight="50vh"
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                        >
                            <Zoom in timeout={800}>
                                <Box sx={{ color: theme.palette.text.disabled }}>
                                    <ProductionQuantityLimitsOutlined sx={{ fontSize: 120, mb: 2 }} />
                                    <Typography variant="h5" component="div" sx={{ mb: 2 }}>
                                        {role === "my_products" ? t('youdidnotcretaedproductyet') : t(`noProductFound`)}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t('tryAdjustingFilters')}
                                    </Typography>
                                </Box>
                            </Zoom>
                        </Box>
                    </Fade>
                ) : (
                    <Grid container spacing={3}>
                        {paginatedProducts?.map((product, index) => (
                            <AnimatedGrid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={4}
                                key={product.id || index}
                            >
                                <Grow in timeout={(index * 100) + 500}>
                                    <div>
                                        <ProductCard
                                            product={product}
                                            onClick={() => setSelectedProduct(product)}
                                            role={role}
                                            user={user}
                                            refresh={refresh}
                                        />
                                    </div>
                                </Grow>
                            </AnimatedGrid>
                        ))}
                    </Grid>
                )}
            </StyledPaper>

            {filteredProducts.length > 0 && (
                <Fade in timeout={800}>
                    <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Pagination
                            count={Math.ceil(filteredProducts.length / itemsPerPage)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary
                                },
                                '& .Mui-selected': {
                                    boxShadow: theme.shadows[2]
                                }
                            }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                            {t('showingResults', {
                                from: (currentPage - 1) * itemsPerPage + 1,
                                to: Math.min(currentPage * itemsPerPage, filteredProducts.length),
                                total: filteredProducts.length
                            })}
                        </Typography>
                    </Box>
                </Fade>
            )}

            {selectedProduct && (
                <ProductDetailModal
                    product={selectedProduct}
                    open={!!selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                    role={role}
                    onEdit={handleSetProduct}
                    onDelete={handleDelete}
                    user={user}
                />
            )}
            <ProductForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={(formData) => {
                    handleUpdateProduct(formData);
                    setFormOpen(false);
                }}
                product={editProduct}
                uploading={false}
                user={user}
                mode="edit"
            />

        </Box>
    );
}

FilteredProducts.propTypes = {
    filteredProducts: PropTypes.array.isRequired,
    user: PropTypes.shape({
        eventId: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired
    }).isRequired,
    itemsPerPage: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    refresh: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};
